import { Button } from "@/components/ui/button";
import { SAMPLE_DATASETS, type SampleDataset } from "@/services/sampleData";
import { cn } from "@/utils/cn";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import {
  Calendar,
  CheckCircle2,
  Database,
  LineChart,
  Sparkles,
  TrendingUp,
  X,
} from "lucide-react";
import { useState } from "react";

interface SampleDataSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (dataset: SampleDataset) => void;
  isLoading?: boolean;
}

export function SampleDataSelector({ open, onOpenChange, onSelect, isLoading }: SampleDataSelectorProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const handleUse = () => {
    const dataset = SAMPLE_DATASETS.find((d) => d.id === selectedId);
    if (dataset) {
      onSelect(dataset);
      onOpenChange(false); // Close the modal immediately after selection
    }
  };
  const iconFor = (symbol: string) => {
    switch (symbol) {
      case "BTC_USD":
        return "₿";
      case "ETH_USD":
        return "Ξ";
      case "SOL_USD":
        return "◎";
      case "XRP_USD":
        return "✕";
      default:
        return <TrendingUp className="w-5 h-5" />;
    }
  };
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay asChild>
          <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />
        </DialogPrimitive.Overlay>
        <DialogPrimitive.Content asChild>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed inset-0 z-50 grid place-items-center p-4"
          >
            <div
              className={cn(
                "w-full max-w-2xl max-h-[85vh] overflow-y-auto",
                "rounded-xl border border-dark-700 bg-dark-900 shadow-2xl",
                "focus:outline-none"
              )}
            >
            <div className="p-5 border-b border-dark-700 flex items-center justify-between">
              <DialogPrimitive.Title asChild>
                <div className="flex items-center gap-2 text-lg font-semibold text-white">
                  <Sparkles className="w-5 h-5 text-brand-purple" />
                  Get Started with Sample Data
                </div>
              </DialogPrimitive.Title>
              <DialogPrimitive.Close asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                  <X className="w-4 h-4" />
                </Button>
              </DialogPrimitive.Close>
            </div>
            <div className="p-5 space-y-3">
              {SAMPLE_DATASETS.map((dataset, idx) => {
                const isSelected = selectedId === dataset.id;
                return (
                  <motion.button
                    key={dataset.id}
                    type="button"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    onClick={() => setSelectedId(dataset.id)}
                    disabled={isLoading}
                    className={cn(
                      "group relative w-full overflow-hidden rounded-2xl border text-left transition-all duration-200",
                      isSelected
                        ? "border-brand-blue/40 bg-gradient-to-r from-brand-blue/10 via-brand-purple/10 to-brand-blue/20 shadow-[0_18px_45px_rgba(59,130,246,0.20)]"
                        : "border-white/5 bg-dark-800/60 hover:border-brand-blue/30 hover:bg-dark-800",
                      isLoading && "opacity-50 cursor-not-allowed"
                    )}
                    aria-pressed={isSelected}
                  >
                    <div className="relative z-10 flex items-center gap-4 px-5 py-4 sm:px-6">
                      <div
                        className={cn(
                          "grid h-12 w-12 place-items-center rounded-xl border text-xl font-semibold",
                          isSelected
                            ? "border-brand-blue/50 bg-brand-blue/20 text-brand-blue"
                            : "border-white/10 bg-white/5 text-white/70 group-hover:text-white",
                        )}
                      >
                        {iconFor(dataset.symbol)}
                      </div>

                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-white">
                          {dataset.displayName}
                        </h3>
                        <p className="text-xs text-white/50">{dataset.description}</p>
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-white/50">
                          <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1">
                            <LineChart className="h-3 w-3" /> {dataset.timeframe}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1">
                            <Database className="h-3 w-3" /> Demo dataset
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1">
                            <Calendar className="h-3 w-3" /> Daily timeframe
                          </span>
                        </div>
                      </div>

                      <div className="ml-auto">
                        {isSelected ? (
                          <div className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Selected
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-white/60 group-hover:border-brand-blue/40 group-hover:text-white">
                            Choose dataset
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className={cn(
                        "absolute inset-0 rounded-2xl bg-gradient-to-r from-brand-blue/15 via-transparent to-brand-blue/10 opacity-0 transition-opacity duration-200",
                        isSelected ? "opacity-100" : "group-hover:opacity-60",
                      )}
                    />
                  </motion.button>
                );
              })}
            </div>
            <div className="p-5 border-t border-dark-700 flex items-center justify-between">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="w-3 h-3" /> For quick testing and setup</p>
              <div className="flex gap-3">
                <DialogPrimitive.Close asChild>
                  <Button variant="outline" className="border-dark-700 hover:bg-dark-800">Cancel</Button>
                </DialogPrimitive.Close>
                <Button onClick={handleUse} disabled={!selectedId || isLoading} className="bg-gradient-to-r from-brand-purple to-brand-blue">Use Sample Data</Button>
              </div>
            </div>
            </div>
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
